import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GameState, positionToSquare } from './game/logic/gameState';
import {
  DEFAULT_CLOCK_CONFIG,
  advanceClock,
  completeClockMove,
  createClockState,
  describeClockConfig,
  formatClock,
  normalizeClockConfig,
  stopClock,
} from './game/logic/chessClock';
import { classifyOpening } from './game/logic/openingClassifier';
import { evaluatePosition } from './game/logic/positionEvaluator';
import Board from './components/Board';
import ClockSetup from './components/ClockSetup';
import EvaluationBar from './components/EvaluationBar';
import GameReviewControls from './components/GameReviewControls';
import Piece from './components/Piece';
import PositionTools, { PositionImportDialog } from './components/PositionTools';
import './styles/chess.css';

const PROMOTION_OPTIONS = ['queen', 'rook', 'bishop', 'knight'];
const samePosition = (a, b) => a && b && a[0] === b[0] && a[1] === b[1];

const TurnRail = ({ color, active, timeMs, running }) => {
  const urgent = timeMs !== null && timeMs <= 30000;
  const displayTime = timeMs === null ? '--:--' : formatClock(timeMs);
  return (
    <div className={`turn-rail${active ? ' turn-rail--active' : ''}${urgent ? ' turn-rail--urgent' : ''}`}>
      <span className={`player-token player-token--${color}`} aria-hidden="true" />
      <span className="turn-rail__identity">
        <span className="turn-rail__name">{color === 'white' ? 'White' : 'Black'}</span>
        <span className="turn-rail__state">{active && running ? 'Clock running' : 'Waiting'}</span>
      </span>
      <time className="player-clock" aria-label={`${color} clock, ${displayTime}`}>
        {displayTime}
      </time>
    </div>
  );
};

const MoveRecord = ({ moves, currentPly = null }) => {
  const rows = useMemo(() => {
    const pairs = [];
    moves.forEach((move, index) => {
      if (index % 2 === 0) pairs.push({ number: Math.floor(index / 2) + 1, white: move, black: null });
      else pairs[pairs.length - 1].black = move;
    });
    return pairs;
  }, [moves]);

  if (rows.length === 0) {
    return (
      <div className="record-empty">
        <span className="record-empty__mark" aria-hidden="true">♙</span>
        <p>Your moves will appear here.</p>
      </div>
    );
  }

  return (
    <ol className="move-list" aria-label="Move history">
      {rows.map((row) => (
        <li className="move-row" key={row.number}>
          <span className="move-number">{row.number}.</span>
          <span className={currentPly === (row.number * 2) - 1 ? 'move-entry move-entry--current' : 'move-entry'} aria-current={currentPly === (row.number * 2) - 1 ? 'step' : undefined}>
            {row.white?.notation}
          </span>
          <span className={currentPly === row.number * 2 ? 'move-entry move-entry--current' : 'move-entry'} aria-current={currentPly === row.number * 2 ? 'step' : undefined}>
            {row.black?.notation ?? ''}
          </span>
        </li>
      ))}
    </ol>
  );
};

const Chess = () => {
  const [gameState, setGameState] = useState(() => new GameState());
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [promotionRequest, setPromotionRequest] = useState(null);
  const [clockConfig, setClockConfig] = useState(() => normalizeClockConfig(DEFAULT_CLOCK_CONFIG));
  const [clockState, setClockState] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [showClockSetup, setShowClockSetup] = useState(true);
  const [showPositionImport, setShowPositionImport] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewPly, setReviewPly] = useState(0);
  const lastTickRef = useRef(null);
  const opening = useMemo(() => classifyOpening(gameState.moveHistory), [gameState.moveHistory]);
  const displayedGameState = useMemo(
    () => (reviewMode ? gameState.getStateAtPly(reviewPly) : gameState),
    [gameState, reviewMode, reviewPly],
  );
  const displayedOpening = useMemo(
    () => classifyOpening(displayedGameState.moveHistory),
    [displayedGameState],
  );
  const evaluation = useMemo(() => evaluatePosition(displayedGameState), [displayedGameState]);

  const commitMove = useCallback((toPosition, promotionType = 'queen') => {
    if (!gameStarted || !clockState || gameState.isGameOver()) return;

    const now = performance.now();
    const elapsed = lastTickRef.current === null ? 0 : now - lastTickRef.current;
    const syncedClock = advanceClock(clockState, elapsed);
    lastTickRef.current = now;
    if (syncedClock.timeoutColor) {
      setClockState(syncedClock);
      return;
    }

    const moverColor = gameState.currentPlayer;
    const newState = gameState.clone();
    if (!newState.movePiece(toPosition, promotionType)) return;

    setGameState(newState);
    setClockState(completeClockMove(syncedClock, moverColor));
    setSelectedPosition(null);
    setPromotionRequest(null);
  }, [clockState, gameStarted, gameState]);

  const handleSquareClick = useCallback((row, col) => {
    if (!gameStarted || reviewMode || promotionRequest || gameState.isGameOver()) return;

    const target = [row, col];
    const targetPiece = gameState.getPiece(target);
    const isLegalTarget = gameState.possibleMoves.some((move) => samePosition(move, target));

    if (gameState.selectedPiece && isLegalTarget) {
      const movingPiece = gameState.getPiece(gameState.selectedPiece);
      if (movingPiece?.type === 'pawn' && (row === 1 || row === 8)) {
        setPromotionRequest({ to: target, color: movingPiece.color });
        return;
      }
      commitMove(target);
      return;
    }

    const newState = gameState.clone();
    if (targetPiece?.color === gameState.currentPlayer && newState.selectPiece(target)) {
      setSelectedPosition(target);
    } else {
      newState.clearSelection();
      setSelectedPosition(null);
    }
    setGameState(newState);
  }, [commitMove, gameStarted, gameState, promotionRequest, reviewMode]);

  const resetGame = useCallback(() => {
    setGameState(new GameState());
    setSelectedPosition(null);
    setPromotionRequest(null);
    setClockState(null);
    setGameStarted(false);
    setShowClockSetup(true);
    setReviewMode(false);
    setReviewPly(0);
    lastTickRef.current = null;
  }, []);

  const startGame = useCallback((config) => {
    const normalizedConfig = normalizeClockConfig(config);
    setClockConfig(normalizedConfig);
    setGameState(new GameState());
    setSelectedPosition(null);
    setPromotionRequest(null);
    setClockState(createClockState(normalizedConfig));
    setGameStarted(true);
    setShowClockSetup(false);
    setReviewMode(false);
    setReviewPly(0);
    lastTickRef.current = performance.now();
  }, []);

  const importPosition = useCallback((fen) => {
    try {
      const importedState = GameState.fromFen(fen);
      const importedClock = createClockState(clockConfig);
      importedClock.activeColor = importedState.currentPlayer;
      setGameState(importedState);
      setSelectedPosition(null);
      setPromotionRequest(null);
      setClockState(importedClock);
      setGameStarted(true);
      setShowClockSetup(false);
      setShowPositionImport(false);
      setReviewMode(false);
      setReviewPly(0);
      lastTickRef.current = performance.now();
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message || 'This FEN position could not be loaded.' };
    }
  }, [clockConfig]);

  const exportPgn = useCallback(() => {
    const today = new Date();
    const date = [today.getFullYear(), today.getMonth() + 1, today.getDate()]
      .map((part, index) => (index === 0 ? part : String(part).padStart(2, '0')))
      .join('.');
    const pgn = gameState.toPgn({
      date,
      opening: opening?.name,
      eco: opening?.eco,
    });
    const blob = new Blob([pgn], { type: 'application/x-chess-pgn;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `quiet-board-${date.replaceAll('.', '-')}.pgn`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [gameState, opening]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  }, []);

  const gameOver = gameState.isGameOver();
  const reviewedMove = reviewPly > 0 ? gameState.moveHistory[reviewPly - 1] : null;

  const startReview = useCallback(() => {
    setReviewPly(gameState.moveHistory.length);
    setReviewMode(true);
  }, [gameState.moveHistory.length]);

  const exitReview = useCallback(() => {
    setReviewPly(gameState.moveHistory.length);
    setReviewMode(false);
  }, [gameState.moveHistory.length]);

  const previousReviewMove = useCallback(() => {
    setReviewPly((current) => Math.max(0, current - 1));
  }, []);

  const nextReviewMove = useCallback(() => {
    setReviewPly((current) => Math.min(gameState.moveHistory.length, current + 1));
  }, [gameState.moveHistory.length]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() === 'f' && !event.metaKey && !event.ctrlKey && !event.altKey) toggleFullscreen();
      if (!reviewMode || ['INPUT', 'TEXTAREA'].includes(event.target.tagName)) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        previousReviewMove();
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        nextReviewMove();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextReviewMove, previousReviewMove, reviewMode, toggleFullscreen]);

  useEffect(() => {
    if (!showClockSetup && !promotionRequest && !showPositionImport) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [promotionRequest, showClockSetup, showPositionImport]);

  useEffect(() => {
    if (!gameStarted || showClockSetup || gameOver || !clockState?.isRunning) return undefined;

    lastTickRef.current = performance.now();
    const interval = window.setInterval(() => {
      const now = performance.now();
      const elapsed = now - lastTickRef.current;
      lastTickRef.current = now;
      setClockState((current) => advanceClock(current, elapsed));
    }, 100);

    return () => window.clearInterval(interval);
  }, [clockState?.isRunning, gameOver, gameStarted, showClockSetup]);

  useEffect(() => {
    if (!clockState?.timeoutColor || gameOver) return;
    const winner = clockState.timeoutColor === 'white' ? 'black' : 'white';
    const winnerName = winner === 'white' ? 'White' : 'Black';
    setGameState((current) => {
      const next = current.clone();
      next.clearSelection();
      next.status = { type: 'timeout', reason: 'timeout', winner, message: `Time out — ${winnerName} wins` };
      return next;
    });
    setSelectedPosition(null);
    setPromotionRequest(null);
  }, [clockState?.timeoutColor, gameOver]);

  useEffect(() => {
    if (gameOver) setClockState((current) => stopClock(current));
  }, [gameOver]);

  useEffect(() => {
    window.render_game_to_text = () => JSON.stringify({
      mode: showClockSetup ? 'clock-setup' : reviewMode ? 'review' : gameOver ? 'game-over' : 'playing',
      coordinateSystem: 'Files a-h run left to right; ranks 8-1 run top to bottom; White is at the bottom.',
      currentPlayer: displayedGameState.currentPlayer,
      status: displayedGameState.status,
      resultStatus: gameState.status,
      selectedSquare: displayedGameState.selectedPiece ? positionToSquare(displayedGameState.selectedPiece) : null,
      legalTargets: displayedGameState.possibleMoves.map(positionToSquare),
      pieces: displayedGameState.board.flatMap((row, rowIndex) => row.map((piece, colIndex) => (
        piece && rowIndex > 0 && colIndex > 0
          ? { square: positionToSquare([rowIndex, colIndex]), type: piece.type, color: piece.color }
          : null
      ))).filter(Boolean),
      lastMove: displayedGameState.lastMove ? {
        from: positionToSquare(displayedGameState.lastMove.from),
        to: positionToSquare(displayedGameState.lastMove.to),
        notation: displayedGameState.moveHistory.at(-1)?.notation ?? null,
      } : null,
      moveCount: displayedGameState.moveHistory.length,
      totalMoveCount: gameState.moveHistory.length,
      review: reviewMode ? { ply: reviewPly, totalPlies: gameState.moveHistory.length } : null,
      fen: displayedGameState.getFen(),
      evaluation,
      opening: displayedOpening,
      clock: clockState ? {
        config: clockState.config,
        activeColor: clockState.activeColor,
        isRunning: clockState.isRunning,
        remainingMs: {
          white: Math.round(clockState.remainingMs.white),
          black: Math.round(clockState.remainingMs.black),
        },
        display: {
          white: formatClock(clockState.remainingMs.white),
          black: formatClock(clockState.remainingMs.black),
        },
        moveCounts: clockState.moveCounts,
        stageAwarded: clockState.stageAwarded,
        timeoutColor: clockState.timeoutColor,
      } : null,
    });
    window.advanceTime = (milliseconds) => {
      lastTickRef.current = performance.now();
      setClockState((current) => advanceClock(current, milliseconds));
    };
    return () => {
      delete window.render_game_to_text;
      delete window.advanceTime;
    };
  }, [clockState, displayedGameState, displayedOpening, evaluation, gameOver, gameState, reviewMode, reviewPly, showClockSetup]);

  const statusTone = reviewMode
    ? 'review'
    : ['check', 'checkmate', 'timeout'].includes(gameState.status.type) ? 'danger' : gameOver ? 'complete' : 'live';

  return (
    <main className="chess-page">
      <header className="site-header">
        <a className="brand" href="#game" aria-label="Quiet Board home">
          <span className="brand__mark" aria-hidden="true">♞</span>
          <span>Quiet Board</span>
        </a>
        <div className="header-actions">
          <span className="rules-live"><span aria-hidden="true" />All rules active</span>
          <button className="icon-button" type="button" onClick={toggleFullscreen} aria-label="Toggle fullscreen" title="Fullscreen (F)">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5m13 5h5v-5" /></svg>
          </button>
        </div>
      </header>

      <section className="game-layout" id="game" aria-label="Local chess game">
        <div className="board-stage">
          <TurnRail
            color="black"
            active={gameStarted && clockState?.activeColor === 'black' && !gameOver}
            timeMs={clockState?.remainingMs.black ?? null}
            running={clockState?.isRunning ?? false}
          />
          <div className="board-with-evaluation">
            <EvaluationBar evaluation={evaluation} />
            <div className="board-frame">
              <Board
                gameState={displayedGameState}
                selectedPosition={reviewMode ? null : selectedPosition}
                onSquareClick={handleSquareClick}
              />
            </div>
          </div>
          <TurnRail
            color="white"
            active={gameStarted && clockState?.activeColor === 'white' && !gameOver}
            timeMs={clockState?.remainingMs.white ?? null}
            running={clockState?.isRunning ?? false}
          />
        </div>

        <aside className="game-panel">
          <div className={`game-status game-status--${statusTone}`} key={reviewMode ? `review-${reviewPly}` : `${gameState.status.type}-${gameState.moveHistory.length}`} role="status" aria-live="polite">
            <span className="game-status__eyebrow">
              {reviewMode
                ? `Review ${reviewPly} of ${gameState.moveHistory.length}`
                : gameState.isGameOver() ? 'Game complete' : gameState.status.type === 'check' ? 'King under attack' : `Move ${gameState.fullmoveNumber}`}
            </span>
            <h1>{reviewMode ? (reviewedMove ? `After ${reviewedMove.notation}` : 'Starting position') : gameState.status.message}</h1>
            <p>
              {reviewMode
                ? 'Step through the game to inspect the board and evaluation at every move.'
                : gameState.status.type === 'check'
                ? 'Choose a legal king move, capture the attacker, or block the attack.'
                : gameState.isGameOver()
                  ? gameState.moveHistory.length > 0
                    ? 'Review every move or start a new game when you are ready.'
                    : 'Start a new game when you are ready for another round.'
                  : 'Select a piece to see every legal move.'}
            </p>
          </div>

          {gameOver && !reviewMode && gameState.moveHistory.length > 0 && (
            <button className="review-start-button" type="button" onClick={startReview}>
              <span aria-hidden="true">↶</span>
              Review game
            </button>
          )}

          {reviewMode && (
            <GameReviewControls
              ply={reviewPly}
              totalPlies={gameState.moveHistory.length}
              onPrevious={previousReviewMove}
              onNext={nextReviewMove}
              onExit={exitReview}
            />
          )}

          {gameStarted && displayedGameState.moveHistory.length > 0 && (
            <div className={`opening-identity${displayedOpening ? '' : ' opening-identity--unknown'}`} key={displayedOpening?.name ?? 'unknown'}>
              <span className="opening-identity__label">
                Opening
                {displayedOpening && <b>{displayedOpening.eco}</b>}
              </span>
              <strong>{displayedOpening?.family ?? 'Unclassified position'}</strong>
              {displayedOpening?.variation && <p>{displayedOpening.variation}</p>}
            </div>
          )}

          {clockState && (
            <div className="time-control-summary">
              <span>Time control</span>
              <strong>{describeClockConfig(clockConfig)}</strong>
            </div>
          )}

          <section className="record-section" aria-labelledby="record-title">
            <div className="section-heading">
              <h2 id="record-title">Game record</h2>
              <span>{gameState.moveHistory.length} {gameState.moveHistory.length === 1 ? 'move' : 'moves'}</span>
            </div>
            <div className="record-scroll">
              <MoveRecord moves={gameState.moveHistory} currentPly={reviewMode ? reviewPly : null} />
            </div>
          </section>

          <PositionTools onExportPgn={exportPgn} onOpenImport={() => setShowPositionImport(true)} />

          <div className="rule-notes" aria-label="Enabled chess rules">
            <span>Check enforced</span>
            <span>Castling</span>
            <span>En passant</span>
            <span>Promotion</span>
            <span>Draw rules</span>
            <span>Clock timeout</span>
          </div>

          <button className="new-game-button" type="button" onClick={resetGame}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4v6h6M20 20v-6h-6M5.6 15a7 7 0 0 0 11.8 2M18.4 9A7 7 0 0 0 6.6 7" /></svg>
            New game
          </button>
          <p className="keyboard-note">Press <kbd>F</kbd> for fullscreen</p>
        </aside>
      </section>

      {showClockSetup && (
        <ClockSetup
          initialConfig={clockConfig}
          onStart={startGame}
          onImportPosition={() => setShowPositionImport(true)}
        />
      )}

      {showPositionImport && (
        <PositionImportDialog
          currentFen={gameState.getFen()}
          onCancel={() => setShowPositionImport(false)}
          onImport={importPosition}
        />
      )}

      {promotionRequest && (
        <div className="promotion-backdrop" role="presentation">
          <section className="promotion-dialog" role="dialog" aria-modal="true" aria-labelledby="promotion-title">
            <span className="promotion-dialog__eyebrow">Pawn promotion</span>
            <h2 id="promotion-title">Choose your piece</h2>
            <div className="promotion-options">
              {PROMOTION_OPTIONS.map((type) => (
                <button key={type} type="button" onClick={() => commitMove(promotionRequest.to, type)} aria-label={`Promote to ${type}`}>
                  <Piece type={type} color={promotionRequest.color} />
                  <span>{type}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </main>
  );
};

export default Chess;
